<template>
  <div class="page-content">
    <router-link
      tag="a"
      to="/projects"
      class="back-link"
    >
      Back to Projects
    </router-link>
    <header class="page-header">
      <div class="page-title">
        <div>
          <div
            class="project-dot"
            :style="{'background-color': project.colour}">
          </div>
          <h1>{{project.name}}</h1>
        </div>
        <SettingsIcon @show-project-form="displayProjectForm" />
      </div>

      <div class="button-group">
        <Button
          :title="'Add A User'"
          :type="'raised'"
          :colorTheme="'primary'"
          @button-click="displayNewUserForm"
        />
        <Button
          class="uploadCsv-button"
          :title="'Upload CSV'"
          :type="'raised'"
          :colorTheme="'primary'"
          @button-click="displayCsvUploadForm"
        />
      </div>
    </header>

    <div class="page-sub-header">
      <input
        class="search"
        v-model="searchText"
        type="text"
        placeholder="Search Login ID"
      >
      <div
        class="filter-icon-container"
        title="Filters"
        :style="{ 'background': filterBackgroundColor }"
      >
        <FilterIcon
          class="filter-icon"
          :hasActiveFilters="activeFilters.length > 0"
          @click.native="displayAddFilterForm"
        />
      </div>
    </div>

    <EditProject
      v-if="showProjectForm"
      :project="project"
      :projectId="id"
      @hide-project-form="hideProjectForm"
    />
    <UserForm
      v-if="showUserForm"
      :projectId="id"
      @hide-dialog="hideUserForm"
      @save-user="addUser"
    />
    <UploadCSV
      v-if="showCsvUploadForm"
      :projectId="id"
      @hide-dialog="hideCsvUploadForm"
      @upload-CSV="uploadCsv"
    />
    <Users
      v-if="showUsers"
      :users="filteredUsers"
      :showBrokeUsers="project.showBrokeUsers"
      :searchText="searchText"
    />
    <ActiveFilters
      :showNav="showAddFilterForm"
      :projectFilters="project.filters"
      :activeFilters="activeFilters"
      @hideNav="hideActiveFiltersNav"
      @saveActiveFilters="saveActiveFilters"
    />
  </div>
</template>

<script>
import firebase from 'firebase/app'
import 'firebase/database'
import Papa from 'papaparse'
import Users from '@/components/user/Users.vue'
import UserForm from '@/components/user/UserForm.vue'
import ActiveFilters from '@/components/project/ActiveFilters'
import UploadCSV from '@/components/UploadCSV.vue'
import SettingsIcon from '@/components/assets/SettingsIcon.vue'
import EditProject from '@/components/project/EditProject';
import Button from '@/components/form-elements/Button';
import { mapState, mapActions } from 'vuex'
import FilterIcon from '@/components/assets/FilterIcon';

export default {
  name: 'Project',
  components: {
    FilterIcon,
    EditProject,
    Users,
    UserForm,
    UploadCSV,
    SettingsIcon,
    Button,
    ActiveFilters
  },
  props: {
    id: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      selectedProject: {},
      filters: [],
      showUserForm: false,
      showCsvUploadForm: false,
      showProjectForm: false,
      showUsers: false,
      searchText: '',
      showAddFilterForm: false,
      activeFilters: [],
      defaultFilterBackground: 'transparent',
      filterBackgroundColor: this.defaultFilterBackground
    }
  },
  computed: {
    ...mapState('projects', {
      project: state => state.project,
      users: state => state.users
    }),
    filteredUsers() {
      if (this.activeFilters.length > 0) {
        this.filterBackgroundColor = this.project.colour
        return this.users.filter(user => this.hasAllActiveFilters(user))
      } else {
        this.filterBackgroundColor = this.defaultFilterBackground
        return this.users
      }
    }
  },
  created() {
    this.fetchProject(this.id)
      .then(project => {
        this.fetchUsers(project.id)
          .then(() => {
            this.showUsers = true
          })
      })
  },
  methods: {
    ...mapActions('projects', {
      fetchProject: 'fetchProject',
      fetchUsers: 'fetchUsers'
    }),
    displayNewUserForm() {
      this.showUserForm = true
    },
    displayCsvUploadForm() {
      this.showCsvUploadForm = true
    },
    displayProjectForm() {
      this.showProjectForm = true
    },
    displayAddFilterForm() {
      this.showAddFilterForm = true
    },
    hideUserForm() {
      this.showUserForm = false
    },
    hideCsvUploadForm() {
      this.showCsvUploadForm = false
    },
    hideProjectForm() {
      this.showProjectForm = false
    },
    hideActiveFiltersNav() {
      this.showAddFilterForm = false
    },
    saveActiveFilters(updatedActiveFilters) {
      this.activeFilters = updatedActiveFilters
    },
    hasAllActiveFilters(user) {
      return this.activeFilters.every(activeFilter => this.hasAllFilters(this.matchingUserFilter(activeFilter, user), activeFilter))
    },
    hasAllFilters(matchingUserFilter, activeFilter) {
      return matchingUserFilter ? this.containsAllValues(matchingUserFilter, activeFilter) : false
    },
    containsAllValues(userFilter, activeFilter) {
      return activeFilter.values.every(value => userFilter.values.includes(value))
    },
    matchingUserFilter(activeFilter, user) {
      if(!user.filters) return false;
      return user.filters.find(filter => filter.name === activeFilter.name)
    },
    addUser(user) {
      this.$store.dispatch('projects/addUser', user)
        .then(() => this.showUserForm = false)
    },
    uploadCsv($event) {
      const file = $event.target.files[0]
      const config = {
        delimiter: '',
        newline: '',
        header: true,
        complete: (results) => {
          this.saveCsvUsers(results)
          this.showCsvUploadForm = false
        },
        error: (error) => {
          alert("Error: " + error)
        }

      }
      Papa.parse(file, config)
    },
    saveCsvUsers(results) {
      results.data.forEach((user) => {
        user.projectId = this.id
        firebase.database().ref('users').push(user)
      })
    }
  }
}
</script>

<style scoped lang="scss" rel="stylesheet/scss">
@import '../../assets/styles/colors';
@import '../../assets/styles/mixin';
@import '../../assets/styles/page';
@import '../../assets/styles/input';

.project-dot {
  display: block;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  margin-right: 8px;
}

.uploadCsv-button {
  display: none;

  @media (min-width: 600px) {
    display: block;
  }
}

.page-sub-header {
  @include flex(row, flex-start, center);
}

.filter-icon-container {
  @include flex(row, center, center);
  width: 44px;
  height: 44px;
  margin-left: 20px;
  border-radius: 4px;

  @media (min-width: 600px) {
    width: 30px;
    height: 30px;
  }

  &.active-filter {
    background-color: $primary-shadow;
  }
}

.filter-icon {
  cursor: pointer;
}
</style>



// WEBPACK FOOTER //
// src/components/project/Project.vue